import { buttonStyle } from 'components/buttons/buttonStyle';
import { FormattedDate } from 'components/misc/FormattedDate';
import BasicTable from 'components/tables/BasicTable';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { Button } from 'components/ui/button';
import { Tooltip } from 'components/ui/tooltip';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { InvoiceNumber, InvoiceStatus } from 'pages/invoices/InvoiceTableCells';
import { useMemo } from 'react';
import { ClassifierData } from 'types/classifiers';
import { InvoiceData, InvoiceDataListItem, InvoiceMinDto } from 'types/invoices';

import { FormattedMessage } from 'react-intl';
import { t } from 'utils/getTranslations';

type Props = {
    classifiers: Record<string, ClassifierData>;
    invoice: InvoiceData;
};

const PreviousInvoicesCard = ({ classifiers, invoice }: Props) => {
    const noRecordsElement = (
        <p className="px-4 py-2 text-[12px] leading-5">
            <FormattedMessage id="invoices.no-invoices-found" />
        </p>
    );
    const { previousInvoices } = invoice;

    const columns = useMemo<DataTableColumnDef<InvoiceMinDto>[]>(
        () => [
            {
                header: t('invoices.invoice'),
                id: 'invoice',
                cell: ({ row }) => (
                    <InvoiceNumber invoice={row.original as unknown as InvoiceDataListItem} target="_blank" />
                ),
                meta: {
                    width: 0,
                },
            },
            {
                header: t('invoices.invoice-date'),
                accessorKey: 'invoiceDate',
                cell: ({ getValue }) => {
                    const invoiceDate = getValue<InvoiceMinDto['invoiceDate']>();
                    return invoiceDate ? <FormattedDate value={invoiceDate} fallback="" /> : null;
                },
                meta: {
                    width: 0,
                },
            },
            {
                header: t('invoices.status'),
                id: 'invoiceStatusTypeCode',
                cell: ({ row }) => (
                    <InvoiceStatus invoice={row.original as unknown as InvoiceDataListItem} classifiers={classifiers} />
                ),
                meta: {
                    width: 0,
                },
            },
            {
                header: t('invoices.total-amount'),
                id: 'totalAmount',
                cell: ({ row }) => {
                    const { paymentCurrencyTypeCode, totalAmountInPaymentCurrency } = row.original as InvoiceMinDto;
                    return <MoneyDisplay amount={totalAmountInPaymentCurrency} currency={paymentCurrencyTypeCode} />;
                },
                meta: {
                    width: 0,
                },
            },
        ],
        [classifiers]
    );

    return (
        <>
            <BasicTable
                columns={columns}
                data={previousInvoices ?? []}
                stickyHeader
                containerClassName="max-h-[325px]"
                noContentJSX={noRecordsElement}
            />
            <div className="py-2">
                <Tooltip content={t('invoices.general-tab.previous-invoices.tooltips.view-unsent-invoices')}>
                    <Button
                        {...buttonStyle(
                            { variant: 'outlined', size: 'extraSmall' },
                            'hover:bg-grey-100 hover:border-divider'
                        )}
                        nativeButton={false}
                        role="link"
                        render={
                            <a
                                href={`/invoices?mf=invoiceMatters.matter.clientParty.id=in=(${invoice.clientParty.id})&qf=invoiceStatusTypeCode=in=(DRAFT,REVIEWED,APPROVED,FINALIZED)`}
                                target="_blank"
                            />
                        }
                    >
                        <FormattedMessage id="invoices.general-tab.previous-invoices.view-unsent-invoices" />
                    </Button>
                </Tooltip>
            </div>
        </>
    );
};

export default PreviousInvoicesCard;
